import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { forkJoin, map, of, Observable, Subject, switchMap, takeUntil, debounceTime, catchError } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { SegmentFilterBuilderComponent } from './components/segment-filter-builder/segment-filter-builder.component';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { CompanySectorApiService } from '../../empresas/services/company-sector-api.service';
import { CompanySizeApiService } from '../../empresas/services/company-size-api.service';
import { CompanyRelationshipTypeApiService } from '../../empresas/services/company-relationship-type-api.service';
import { CompanyCycleApiService } from '../../empresas/services/company-cycle-api.service';
import { CompanyStateLeadApiService } from '../../empresas/services/company-state-lead-api.service';
import { FolderApiService } from '../services/folder-api.service';
import { SegmentService } from '../services/segment-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { VALUELESS_OPERATORS, SEGMENT_TIMEZONES, SegmentFolder, SegmentRequest, Segment, SegmentScheduleRequest, SegmentScheduleResponse, SegmentSeedResponse, FilterRuleRequest, SegmentPreviewContactResponse, SegmentPreviewRequest } from '../models/segment.model';
import { ToastService } from '../../../shared/services/toast.service';
import { CompanySector, CompanySize, CompanyRelationshipType, CompanyCycleResponse, CompanyStateLeadResponse, PageResponseV2 } from '../../empresas/models/empresa.model';
import { Contact, ContactState, ContactCycle, ContactOrigin, ContactAttributeDefinition } from '../../contactos/models/contacto.model';
import { SegmentPreviewPanelComponent } from './components/segment-preview-panel/segment-preview-panel.component';
import { SegmentStaticPreviewComponent } from './components/segment-static-preview/segment-static-preview.component';
import { FrequencySchedulerComponent } from '../../../shared/components/frequency-schedule/frequency-schedule.component';
import { SegmentImportSectionComponent } from '../segment-list/components/segment-import-section/segment-import-section.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { SegmentSetOpsModalService } from '../services/segment-set-ops-modal.service';

@Component({
  selector: 'app-segment-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PageBreadcrumbComponent,
    SegmentFilterBuilderComponent,
    ReactiveFormsModule,
    ComponentCardComponent,
    ButtonComponent,
    FormsModule,
    SegmentPreviewPanelComponent,
    SegmentStaticPreviewComponent,
    FrequencySchedulerComponent,
    SegmentImportSectionComponent,
  ],
  templateUrl: './segment-form.component.html',
})
export class SegmentFormComponent implements OnInit, OnDestroy {

  @ViewChild(SegmentStaticPreviewComponent) staticPreview?: SegmentStaticPreviewComponent;

  isEditing = false;
  hasModifiedFilters = false;
  segmentId: number | null = null;
  existingScheduleId: number | null = null;
  submitted: boolean = false;
  isSaving: boolean = false;
  isDeletingSegment = false;
  isChangingType: boolean = false;
  isLoadingSegment: boolean = false;
  form!: FormGroup;
  showFilters: boolean = false;
  businessId: number = 0;
  currentSegmentType: 'STATIC' | 'DYNAMIC' | null = null;
  showPreviewDrawer = false;
  isDrawerClosing = false;
  previewDrawerTab: 'members' | 'match' | 'no-match' = 'members';
  segmentMembersCount = 0;
  private drawerCloseTimer?: ReturnType<typeof setTimeout>;

  private destroy$ = new Subject<void>();
  private previewAbort$ = new Subject<void>();
  private noCoincidentAbort$ = new Subject<void>();
  private originalRuleIds: number[] = [];

  contactStates: ContactState[] = [];
  contactCycles: ContactCycle[] = [];
  contactOrigins: ContactOrigin[] = [];
  customAttributes: ContactAttributeDefinition[] = [];

  companySectors: CompanySector[] = [];
  companySizes: CompanySize[] = [];
  companyRelationshipTypes: CompanyRelationshipType[] = [];
  companyCycles: CompanyCycleResponse[] = [];
  companyStateLeads: CompanyStateLeadResponse[] = [];

  folders: SegmentFolder[] = [];

  // Preview dinámico
  contactosPreview: SegmentPreviewContactResponse[] = [];
  totalContactosPreview: number = 0;
  totalContactosCrm: number = 0;
  porcentajePreview: number = 0;
  isLoadingPreview: boolean = false;
  previewPage: number = 0;
  previewTotalPages: number = 0;

  // Miembros que ya no coinciden con el filtro (edit mode)
  contactosNoCoinciden: SegmentPreviewContactResponse[] = [];
  totalNoCoinciden: number = 0;
  isLoadingNoCoinciden: boolean = false;
  noCoincidentPage: number = 0;
  noCoincidentTotalPages: number = 0;

  // Modo seed: contactos del preview marcados para excluir
  contactosAExcluirIds: Set<number> = new Set<number>();

  timezones: string[];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private contactoApi: ContactoApiService,
    private segmentService: SegmentService,
    private sectorApi: CompanySectorApiService,
    private sizeApi: CompanySizeApiService,
    private relationApi: CompanyRelationshipTypeApiService,
    private cycleApi: CompanyCycleApiService,
    private stateLeadApi: CompanyStateLeadApiService,
    private folderApi: FolderApiService,
    private confirmDialog: ConfirmDialogService,
    private setOpsModal: SegmentSetOpsModalService,
    private toast: ToastService,
    private authService: AuthService
  ) {
    const tz = this.getDefaultTimezone();
    this.timezones = SEGMENT_TIMEZONES.includes(tz) ? [...SEGMENT_TIMEZONES] : [tz, ...SEGMENT_TIMEZONES];
  }

  ngOnInit(): void {
    // Resolver el negocio del usuario autenticado (patrón canónico).
    // Antes se leía localStorage('bussinesId'), una clave que la app nunca
    // escribe, por lo que siempre caía al fallback businessId=1 y cargaba los
    // catálogos (estados/ciclos/orígenes) de otro negocio.
    this.businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.initForm();
    this.cargarCarpetas();
    this.chargeCatalogos();
    this.cargarTotalCrm();
    this.setupPreviewListener();

    this.setOpsModal.executed$.pipe(takeUntil(this.destroy$)).subscribe(segmentId => {
      if (segmentId === this.segmentId) this.staticPreview?.cargarMiembros(0);
    });

    // Detectar modo edición
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.segmentId = parseInt(id, 10);
        this.isEditing = true;
        this.cargarSegmento(this.segmentId);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.previewAbort$.complete();
    this.noCoincidentAbort$.complete();
    clearTimeout(this.drawerCloseTimer);
  }

  initForm(): void {
    this.form = this.fb.group({
      name: [this.generateDefaultName(), [Validators.required, Validators.minLength(3)]],
      folderId: [''],
      type: ['', Validators.required],
      objectType: ['CONTACT'],
      description: [''],
      rules: this.fb.array([]),

      scheduleConfig: FrequencySchedulerComponent.createScheduleForm(this.fb, this.getDefaultTimezone())
    });
  }

  get scheduleFormGroup(): FormGroup {
    return this.form.get('scheduleConfig') as FormGroup;
  }

  get rules(): FormArray {
    return this.form.get('rules') as FormArray;
  }

  generateDefaultName(): string {
    const now = new Date();
    const d = String(now.getDate()).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const h = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const sec = String(now.getSeconds()).padStart(2, '0');
    return `Segmento sin nombre ${d}/${m}/${now.getFullYear()} ${h}:${min}:${sec}`;
  }

  get isFormInvalidForSave(): boolean {
    const tipo = this.form.get('type')?.value;
    if (tipo === 'STATIC') {
      if (this.form.get('name')?.invalid || this.form.get('type')?.invalid) return true;
      if (this.rules.length > 0 && this.hasPendingRules) return true;
      if (!this.isEditing && this.isLoadingPreview) return true;
      if (!this.isEditing && this.totalContactosPreview === 0) return true;
      if (this.totalContactosPreview > 0 && this.contactosAExcluirIds.size >= this.totalContactosPreview) return true;
      return false;
    }
    if (this.form.invalid || (tipo === 'DYNAMIC' && this.rules.length === 0)) return true;
    if (!this.isEditing && tipo === 'DYNAMIC' && this.isLoadingPreview) return true;
    if (!this.isEditing && tipo === 'DYNAMIC' && this.totalContactosPreview === 0) return true;
    if (tipo === 'DYNAMIC' && this.isSchedulePartiallyConfigured) return true;
    return false;
  }

  get saveHint(): string {
    const tipo = this.form.get('type')?.value;
    if (!tipo) return 'Selecciona el tipo de segmento para continuar.';
    if (this.form.get('name')?.invalid) return 'El nombre del segmento es obligatorio (mínimo 3 caracteres).';
    if (tipo === 'STATIC') {
      if (this.rules.length > 0 && this.hasPendingRules) return 'Algunos criterios de filtrado están incompletos. Completa o elimina cada condición para poder guardar.';
      if (!this.isEditing && this.isLoadingPreview) return 'Calculando preview, espera un momento...';
      if (!this.isEditing && this.totalContactosPreview === 0) {
        return this.rules.length === 0
          ? 'Agrega al menos un criterio de filtrado para poder guardar.'
          : 'Ningún contacto cumple los criterios actuales. Ajusta los filtros para poder guardar.';
      }
      if (this.totalContactosPreview > 0 && this.contactosAExcluirIds.size >= this.totalContactosPreview) {
        return 'Todos los contactos están excluidos. Incluye al menos uno para poder guardar.';
      }
    }
    if (tipo === 'DYNAMIC') {
      if (this.rules.length === 0) return 'Debes agregar al menos una regla de filtrado.';
      if (!this.isEditing && this.isLoadingPreview) return 'Calculando preview, espera un momento...';
      if (!this.isEditing && this.totalContactosPreview === 0) return 'Ningún contacto cumple los criterios actuales. Ajusta los filtros para poder guardar.';
      if (this.isSchedulePartiallyConfigured) return 'La configuración de frecuencia está incompleta. Complétala o limpia la selección de tipo.';
    }
    return '';
  }

  private get hasPendingRules(): boolean {
    const reglasRaw = (this.form.get('rules')?.value ?? []) as FilterRuleRequest[];
    return reglasRaw.some(r =>
      r.property && r.operator && !VALUELESS_OPERATORS.has(r.operator) && !r.valueExpression
    );
  }

  private get isSchedulePartiallyConfigured(): boolean {
    const sc = this.scheduleFormGroup;
    const freq = sc.get('frequencyType')?.value as string;
    if (!freq) return false;
    if (!['MINUTES', 'HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY'].includes(freq)) return false;
    if (freq !== 'MINUTES' && !sc.get('startTime')?.value) return true;
    if ((freq === 'MINUTES' || freq === 'HOURLY') && sc.get('intervalValue')?.value == null) return true;
    if (freq === 'WEEKLY' && ((sc.get('daysOfWeek')?.value as string[] | null) ?? []).length === 0) return true;
    return false;
  }

  // ==============================
  // CARGAR SEGMENTO PARA EDICION
  // ==============================

  async cambiarTipoSegmento(): Promise<void> {
    if (!this.segmentId || this.isChangingType || !this.currentSegmentType) return;

    const targetType: 'STATIC' | 'DYNAMIC' = this.currentSegmentType === 'STATIC' ? 'DYNAMIC' : 'STATIC';

    const warningLines = this.currentSegmentType === 'STATIC'
      ? [
          'Los contactos actuales se conservarán mientras configuras las reglas.',
          'Al evaluar el segmento, los miembros serán reemplazados según las reglas definidas.',
          'El segmento pasará a estado BORRADOR.',
        ]
      : [
          'Las reglas de filtrado automático serán eliminadas.',
          'Los contactos actualmente en el segmento se conservarán como miembros estáticos.',
          'El segmento pasará a estado LISTO.',
        ];

    const ok = await this.confirmDialog.confirm({
      title: `Convertir a ${targetType === 'DYNAMIC' ? 'Dinámico' : 'Estático'}`,
      message: `¿Estás seguro de cambiar el tipo de este segmento?\n\n${warningLines.join('\n')}`,
      confirmText: `Sí, convertir a ${targetType === 'DYNAMIC' ? 'Dinámico' : 'Estático'}`,
      variant: 'warning'
    });
    if (!ok) return;

    this.isChangingType = true;
    this.segmentService.changeSegmentType(this.segmentId, targetType).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updated) => {
        this.currentSegmentType = updated.type;
        this.isChangingType = false;
        this.toast.success({
          title: 'Tipo actualizado',
          message: `El segmento ahora es de tipo ${targetType === 'DYNAMIC' ? 'Dinámico' : 'Estático'}.`,
        });
        this.cargarSegmento(this.segmentId!);
      },
      error: (err) => {
        this.isChangingType = false;
        this.toast.error({ title: 'Error', message: err?.error?.message || 'No se pudo cambiar el tipo del segmento.' });
      },
    });
  }

  irAFrecuencia(): void {
    document.getElementById('frecuencia-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  abrirOperaciones(): void {
    if (this.segmentId) this.setOpsModal.open(this.segmentId);
  }

  onImportCompleted(): void {
    this.staticPreview?.cargarMiembros(0);
  }

  private cargarSegmento(id: number): void {
    // Destroy the filter-builder before fetching so its ngOnInit always reads
    // an already-final FormArray when it gets recreated after load completes.
    this.isLoadingSegment = true;
    this.hasModifiedFilters = false;
    this.noCoincidentAbort$.next();
    this.isLoadingNoCoinciden = false;
    this.contactosNoCoinciden = [];
    this.totalNoCoinciden = 0;
    this.noCoincidentPage = 0;
    this.noCoincidentTotalPages = 0;

    forkJoin({
      segment: this.segmentService.getSegmentById(id),
      rules: this.segmentService.getRulesBySegment(id).pipe(catchError(() => of([])))
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ segment, rules }) => {
        this.currentSegmentType = segment.type;
        this.existingScheduleId = null;

        if (rules.length > 0) {
          this.rules.clear({ emitEvent: false });
          rules.forEach(rule => {
            this.rules.push(this.fb.group({
              groupIndex: [rule.groupIndex],
              groupOperator: [rule.groupOperator],
              property: [rule.property],
              operator: [rule.operator],
              valueExpression: [rule.valueExpression],
              executionOrder: [rule.executionOrder]
            }), { emitEvent: false });
          });
          this.originalRuleIds = rules.map(r => r.id);
          if (segment.type === 'DYNAMIC') this.showFilters = true;
        } else {
          // API returned no rules — preserve what the FormArray already has
          // so the user can see previous filter criteria as reference after conversion.
          if (segment.type === 'DYNAMIC') {
            this.showFilters = this.rules.length > 0;
          }
        }

        this.form.patchValue({
          name: segment.name,
          folderId: segment.folderId || '',
          type: segment.type,
          objectType: segment.objectType || 'CONTACT',
          description: segment.description || ''
        });

        // Reveal filter-builder only after FormArray is fully populated.
        this.isLoadingSegment = false;

        if (segment.type === 'DYNAMIC') {
          this.cargarPreview(0);
          this.cargarScheduleExistente(id);
        } else if (segment.type === 'STATIC' && this.rules.length > 0) {
          this.cargarPreview(0);
        }

        if (segment.type === 'STATIC') {
          this.segmentService.getMembers(id, 0, 1)
            .pipe(takeUntil(this.destroy$))
            .subscribe({ next: res => { this.segmentMembersCount = res.totalElements; } });
        }
      },
      error: (err: any) => {
        this.isLoadingSegment = false;
        if (err?.status === 404) { this.onSegmentGone(); return; }
        this.toast.error({ title: 'Error', message: 'No se pudo cargar la información del segmento.' });
      }
    });
  }

  private cargarScheduleExistente(segmentId: number): void {
    this.segmentService.getSchedule(segmentId).pipe(
      catchError((err) => {
        if (err?.status !== 404) {
          this.toast.error({ title: 'Error', message: 'No se pudo cargar la configuración de evaluación programada.' });
        }
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe((sch) => {
      if (!sch || !sch.id) {
        this.existingScheduleId = null;
        return;
      }
      this.existingScheduleId = sch.id;

      const startTime = sch.runTime ? sch.runTime.substring(0, 5) : '08:00';
      const patch: Record<string, unknown> = {
        frequencyType: sch.frecuency,
        startTime,
        timezone: sch.timezone || this.getDefaultTimezone()
      };
      if (sch.intervalValue != null) patch['intervalValue'] = sch.intervalValue;
      if (sch.frecuency === 'WEEKLY' && Array.isArray(sch.runDays)) {
        patch['daysOfWeek'] = (sch.runDays as Array<string | number>).map(d => String(d));
      }
      if (sch.frecuency === 'MONTHLY' && Array.isArray(sch.runDays) && sch.runDays.length > 0) {
        patch['daysOfMonth'] = sch.runDays.map(Number);
      }
      this.scheduleFormGroup.patchValue(patch);
    });
  }


  // ==============================
  // GUARDAR SEGMENTO
  // ==============================

  guardarSegmento(): void {
    this.submitted = true;

    const tipo = this.form.get('type')?.value;

    if (tipo === 'STATIC') {
      if (this.form.get('name')?.invalid || this.form.get('type')?.invalid) {
        this.form.get('name')?.markAsTouched();
        this.form.get('type')?.markAsTouched();
        return;
      }
      if (this.rules.length > 0 && this.hasPendingRules) return;
    } else {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
    }

    if (tipo === 'DYNAMIC' && this.rules.length === 0) {
      return;
    }

    this.isSaving = true;

    const folderId = this.form.get('folderId')?.value;

    const request: SegmentRequest = {
      bussinesId: this.businessId,
      name: this.form.get('name')?.value.trim(),
      description: this.form.get('description')?.value || undefined,
      type: tipo,
      objectType: this.form.get('objectType')?.value || 'CONTACT',
      folderId: folderId ? Number(folderId) : null,
      rules: tipo === 'DYNAMIC' ? (this.rules.value as FilterRuleRequest[]).map((r) => ({
        groupIndex: r.groupIndex,
        groupOperator: r.groupOperator,
        property: r.property,
        operator: r.operator,
        valueExpression: r.valueExpression,
        executionOrder: r.executionOrder
      })) : []
    };

    const saveObs = this.isEditing && this.segmentId
      ? this.segmentService.updateSegment(this.segmentId, request)
      : this.segmentService.createSegment(request);

    saveObs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (segmentoGuardado: Segment) => {
        const savedId = segmentoGuardado.id!;

        if (tipo === 'STATIC') {
          const hasRules = this.rules.length > 0;

          if (hasRules) {
            const toExclude = [...this.contactosAExcluirIds];
            const userId = this.authService.getCurrentUser()?.id ?? 0;
            const reglasRaw = (this.form.get('rules')?.value ?? []) as FilterRuleRequest[];
            const reglasValidas = reglasRaw.filter(r =>
              r.property && r.operator &&
              (r.valueExpression || VALUELESS_OPERATORS.has(r.operator))
            );
            const objectType = this.form.get('objectType')?.value || 'CONTACT';
            const previewRequest: SegmentPreviewRequest = { rules: reglasValidas, objectType };

            // Order: update rules → exclude non-matching → seed matching → apply manual exclusions
            // excludeNotMatching runs BEFORE seed so contacts deleted by filter are not re-inserted
            // and re-deleted in the same operation, avoiding spurious totalAdded counts.
            this.actualizarReglas(savedId).pipe(
              switchMap(() => this.isEditing && reglasValidas.length > 0 && this.hasModifiedFilters
                ? this.segmentService.excludeNotMatching(savedId, previewRequest)
                : of(null)
              ),
              switchMap(excludeResult =>
                this.segmentService.seedFromRules(savedId).pipe(
                  map(seedResult => ({ excludeResult, seedResult }))
                )
              ),
              switchMap(({ excludeResult, seedResult }) => {
                const applyExclusions$: Observable<any> = toExclude.length > 0
                  ? forkJoin(toExclude.map(contactId =>
                      this.segmentService.excludeMember(savedId, contactId, userId)
                    ))
                  : of(null);
                return applyExclusions$.pipe(map(() => ({ excludeResult, seedResult })));
              }),
              takeUntil(this.destroy$)
            ).subscribe({
              next: ({ excludeResult, seedResult }) => {
                const added = seedResult.totalAdded;
                const excluded = (excludeResult as any)?.excluded ?? 0;
                const parts: string[] = [];
                if (added > 0) parts.push(`${added} contacto${added !== 1 ? 's' : ''} añadido${added !== 1 ? 's' : ''}`);
                if (excluded > 0) parts.push(`${excluded} excluido${excluded !== 1 ? 's' : ''} por filtro`);
                if (toExclude.length > 0) parts.push(`${toExclude.length} excluido${toExclude.length !== 1 ? 's' : ''} manualmente`);
                this.toast.success({
                  title: 'Segmento actualizado',
                  message: parts.length > 0 ? parts.join(', ') + '.' : 'Las reglas se actualizaron. El segmento está al día.'
                });
                this.isSaving = false;
                this.router.navigate(['/segmentos']);
              },
              error: (err) => {
                this.toast.error({
                  title: 'Error al guardar',
                  message: err?.error?.message || 'El segmento se guardó, pero hubo un problema al aplicar los criterios de filtrado.'
                });
                this.isSaving = false;
                this.router.navigate(['/segmentos']);
              }
            });
            return;
          }

          this.isSaving = false;
          this.toast.success({
            title: this.isEditing ? 'Segmento actualizado' : 'Segmento creado',
            message: `El segmento "${this.form.get('name')?.value}" fue guardado correctamente.`
          });
          this.router.navigate(['/segmentos']);
          return;
        }

        if (tipo === 'DYNAMIC') {
          const scheduleReq = this.buildScheduleRequest(savedId);
          const schedule$: Observable<unknown> = scheduleReq
            ? this.upsertSchedule(savedId, scheduleReq)
            : of(null);

          schedule$.pipe(
            switchMap((): Observable<unknown> => this.segmentService.evaluateSegment(savedId)),
            takeUntil(this.destroy$)
          ).subscribe({
            next: () => {
              this.isSaving = false;
              this.toast.success({
                title: this.isEditing ? 'Segmento actualizado' : 'Segmento creado',
                message: `El segmento "${this.form.get('name')?.value}" fue guardado correctamente.`
              });
              this.router.navigate(['/segmentos']);
            },
            error: (err) => {
              this.isSaving = false;
              this.toast.warning({
                title: 'Guardado con advertencia',
                message: err?.error?.message || 'El segmento se guardó, pero hubo un problema al programar o ejecutar la evaluación.'
              });
              this.router.navigate(['/segmentos']);
            }
          });
          return;
        }

        this.isSaving = false;
        this.router.navigate(['/segmentos']);
      },
      error: (err) => {
        this.isSaving = false;
        this.toast.error({ title: 'Error al guardar', message: err?.error?.message || 'No se pudo guardar el segmento. Intenta de nuevo.' });
      }
    });
  }

  private buildScheduleRequest(segmentId: number): SegmentScheduleRequest | null {
    const sc = this.scheduleFormGroup;
    const freq = sc.get('frequencyType')?.value as string;
    const timezone = (sc.get('timezone')?.value as string) || 'UTC';

    if (!freq) return null;
    if (freq !== 'MINUTES' && freq !== 'HOURLY' && freq !== 'DAILY' && freq !== 'WEEKLY' && freq !== 'MONTHLY') {
      return null;
    }

    const req: SegmentScheduleRequest = {
      segmentId,
      frecuency: freq as SegmentScheduleRequest['frecuency'],
      enabled: true,
      timezone
    };

    if (freq !== 'MINUTES') {
      const startTime = sc.get('startTime')?.value as string;
      if (!startTime) return null;
      req.runTime = /^\d{2}:\d{2}$/.test(startTime) ? `${startTime}:00` : startTime;
    }

    if (freq === 'MINUTES' || freq === 'HOURLY') {
      const interval = sc.get('intervalValue')?.value;
      if (interval == null) return null;
      req.intervalValue = Number(interval);
    }

    if (freq === 'WEEKLY') {
      const days = (sc.get('daysOfWeek')?.value as string[] | null) ?? [];
      if (days.length === 0) return null;
      req.runDays = days;
    } else if (freq === 'MONTHLY') {
      const days = (sc.get('daysOfMonth')?.value as number[] | null) ?? [];
      if (days.length > 0) req.runDays = days;
    }

    return req;
  }

  private upsertSchedule(segmentId: number, req: SegmentScheduleRequest): Observable<SegmentScheduleResponse> {
    if (this.isEditing && this.existingScheduleId) {
      return this.segmentService.updateSchedule(this.existingScheduleId, req);
    }
    // Fallback: en edición sin id cacheado, resolvemos contra el backend (404 → crear)
    if (this.isEditing) {
      return this.segmentService.getSchedule(segmentId).pipe(
        catchError(() => of(null as SegmentScheduleResponse | null)),
        switchMap((existing) =>
          existing?.id
            ? this.segmentService.updateSchedule(existing.id, req)
            : this.segmentService.createSchedule(req)
        )
      );
    }
    return this.segmentService.createSchedule(req);
  }

  private actualizarReglas(segmentId: number): Observable<unknown> {
    const reglas = (this.rules.value as FilterRuleRequest[]).filter(r =>
      r.property && r.operator &&
      (r.valueExpression || VALUELESS_OPERATORS.has(r.operator))
    );

    const deleteExisting$: Observable<unknown> = this.isEditing && this.originalRuleIds.length > 0
      ? forkJoin(this.originalRuleIds.map(id => this.segmentService.deleteRule(id)))
      : of(null);

    const createRules$ = reglas.length === 0
      ? of([])
      : forkJoin(reglas.map((r, idx) =>
          this.segmentService.createRule({
            segmentId,
            groupIndex: r.groupIndex,
            groupOperator: r.groupOperator || 'AND',
            property: r.property,
            operator: r.operator,
            valueExpression: r.valueExpression,
            executionOrder: idx,
          })
        ));

    return deleteExisting$.pipe(switchMap(() => createRules$));
  }

  private guardarReglasYSemillar(segmentId: number): Observable<SegmentSeedResponse> {
    return this.actualizarReglas(segmentId).pipe(
      switchMap(() => this.segmentService.seedFromRules(segmentId))
    );
  }

  toggleExcluirContacto(contactoId: number): void {
    const updated = new Set(this.contactosAExcluirIds);
    if (updated.has(contactoId)) {
      updated.delete(contactoId);
    } else {
      updated.add(contactoId);
    }
    this.contactosAExcluirIds = updated;
  }

  // ==============================
  // PREVIEW DINAMICO (server-side)
  // ==============================

  private setupPreviewListener(): void {
    this.form.get('rules')?.valueChanges.pipe(
      debounceTime(800),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.hasModifiedFilters = true;
      this.contactosAExcluirIds = new Set<number>();
      this.cargarPreview(0);
      this.cargarNoCoinciden(0);
    });
  }

  private cargarTotalCrm(): void {
    this.contactoApi.searchContacts({ businessId: this.businessId, isActive: true, page: 0, size: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (res) => { this.totalContactosCrm = res.totalElements || 0; } });
  }

  cargarPreview(page: number): void {
    const reglasRaw = (this.form.get('rules')?.value ?? []) as FilterRuleRequest[];
    const reglasValidas = reglasRaw.filter(r =>
      r.property && r.operator &&
      (r.valueExpression || VALUELESS_OPERATORS.has(r.operator))
    );
    // A rule with property+operator set but a missing required value is "pending".
    // Sending reglasValidas without it would silently ignore that condition and show
    // contacts that don't actually satisfy all the user-defined filters.
    const hasPendingRule = reglasRaw.some(r =>
      r.property && r.operator && !VALUELESS_OPERATORS.has(r.operator) && !r.valueExpression
    );

    if (reglasValidas.length === 0 || hasPendingRule) {
      this.previewAbort$.next();
      this.isLoadingPreview = false;
      this.contactosPreview = [];
      this.totalContactosPreview = 0;
      this.porcentajePreview = 0;
      this.previewPage = 0;
      this.previewTotalPages = 0;
      this.noCoincidentAbort$.next();
      this.isLoadingNoCoinciden = false;
      this.contactosNoCoinciden = [];
      this.totalNoCoinciden = 0;
      this.noCoincidentPage = 0;
      this.noCoincidentTotalPages = 0;
      return;
    }

    // Cancela cualquier request de preview en vuelo antes de iniciar uno nuevo
    this.previewAbort$.next();
    this.isLoadingPreview = true;

    const objectType = this.form.get('objectType')?.value || 'CONTACT';
    const previewRequest: SegmentPreviewRequest = { rules: reglasValidas, objectType };
    if (this.isEditing && this.segmentId && this.currentSegmentType === 'STATIC') previewRequest.excludeSegmentId = this.segmentId;
    this.segmentService.previewSegment(previewRequest, page)
      .pipe(takeUntil(this.previewAbort$), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.contactosPreview = res.content || [];
          this.totalContactosPreview = res.totalElements;
          this.previewPage = res.page;
          this.previewTotalPages = res.totalPages;
          if (this.totalContactosCrm > 0) {
            this.porcentajePreview = Number(((res.totalElements / this.totalContactosCrm) * 100).toFixed(1));
          }
          this.isLoadingPreview = false;
          if (this.isEditing && this.currentSegmentType === 'DYNAMIC') {
            this.segmentMembersCount = res.totalElements;
          }
        },
        error: () => {
          this.isLoadingPreview = false;
          this.contactosPreview = [];
          this.totalContactosPreview = 0;
          this.porcentajePreview = 0;
          this.previewPage = 0;
          this.previewTotalPages = 0;
          this.toast.error({ title: 'Error', message: 'No se pudo cargar la vista previa del segmento.' });
        }
      });
  }

  cargarNoCoinciden(page: number): void {
    if (!this.isEditing || !this.segmentId || this.currentSegmentType !== 'STATIC') return;

    const reglasRaw = (this.form.get('rules')?.value ?? []) as FilterRuleRequest[];
    const reglasValidas = reglasRaw.filter(r =>
      r.property && r.operator &&
      (r.valueExpression || VALUELESS_OPERATORS.has(r.operator))
    );
    const hasPendingRule = reglasRaw.some(r =>
      r.property && r.operator && !VALUELESS_OPERATORS.has(r.operator) && !r.valueExpression
    );

    if (reglasValidas.length === 0 || hasPendingRule) {
      this.noCoincidentAbort$.next();
      this.isLoadingNoCoinciden = false;
      this.contactosNoCoinciden = [];
      this.totalNoCoinciden = 0;
      this.noCoincidentPage = 0;
      this.noCoincidentTotalPages = 0;
      return;
    }

    this.noCoincidentAbort$.next();
    this.isLoadingNoCoinciden = true;

    const objectType = this.form.get('objectType')?.value || 'CONTACT';
    const request: SegmentPreviewRequest = { rules: reglasValidas, objectType };

    this.segmentService.previewNotMatching(this.segmentId, request, page)
      .pipe(takeUntil(this.noCoincidentAbort$), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.contactosNoCoinciden = res.content || [];
          this.totalNoCoinciden = res.totalElements;
          this.noCoincidentPage = res.page;
          this.noCoincidentTotalPages = res.totalPages;
          this.isLoadingNoCoinciden = false;
        },
        error: () => {
          this.isLoadingNoCoinciden = false;
          this.contactosNoCoinciden = [];
          this.totalNoCoinciden = 0;
          this.noCoincidentPage = 0;
          this.noCoincidentTotalPages = 0;
        },
      });
  }

  // ==============================
  // CRON
  // ==============================

  private getDefaultTimezone(): string {
    try {
      const user = JSON.parse(localStorage.getItem('current_user') ?? 'null');
      if (user?.timezone) return user.timezone;
    } catch { /* ignore */ }
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) return detected;
    } catch { /* ignore */ }
    return 'America/Lima';
  }


  // ==============================
  // CARGA DE CATALOGOS
  // ==============================

  private cargarCarpetas(): void {
    this.folderApi.getFolders(0, 100).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => this.folders = res.content || [],
      error: () => {
        this.toast.error({ title: 'Aviso', message: 'No se pudieron cargar las carpetas.' });
      }
    });
  }

  private chargeCatalogos(): void {
    const pageParams = { page: 0, size: 100 };

    forkJoin({
      states: this.contactoApi.getContactStates(this.businessId).pipe(catchError(() => of([]))),
      cycles: this.contactoApi.getContactCycles(this.businessId).pipe(catchError(() => of([]))),
      origins: this.contactoApi.getContactOrigins(this.businessId).pipe(catchError(() => of([]))),
      attributes: this.contactoApi.getAttributeDefinitions(this.businessId).pipe(catchError(() => of([]))),
      sectors: this.sectorApi.getByBusiness(this.businessId).pipe(map((res: CompanySector[]) => res ?? []), catchError(() => of([]))),
      sizes: this.sizeApi.getAll(pageParams).pipe(map((res: PageResponseV2<CompanySize>) => res.content ?? []), catchError(() => of([]))),
      relationships: this.relationApi.getAll(pageParams).pipe(map((res: PageResponseV2<CompanyRelationshipType>) => res.content ?? []), catchError(() => of([]))),
      compCycles: this.cycleApi.getAll(this.businessId, pageParams).pipe(map((res: PageResponseV2<CompanyCycleResponse>) => res.content ?? []), catchError(() => of([]))),
      stateLeads: this.stateLeadApi.getAll(pageParams).pipe(map((res: PageResponseV2<CompanyStateLeadResponse>) => res.content ?? []), catchError(() => of([])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.contactStates = data.states;
          this.contactCycles = data.cycles;
          this.contactOrigins = data.origins;
          this.customAttributes = data.attributes.filter(attr => attr.isActive === true);
          this.companySectors = data.sectors;
          this.companySizes = data.sizes;
          this.companyRelationshipTypes = data.relationships;
          this.companyCycles = data.compCycles;
          this.companyStateLeads = data.stateLeads;
        },
        error: () => {
          this.toast.error({ title: 'Aviso', message: 'No se pudieron cargar los catálogos de filtrado.' });
        }
      });
  }

  // ==============================
  // UI HELPERS
  // ==============================

  mostrarFiltros(): void {
    this.showFilters = true;
  }

  openPreviewDrawer(tab: 'members' | 'match' | 'no-match' = 'members'): void {
    clearTimeout(this.drawerCloseTimer);
    this.previewDrawerTab = tab;
    this.isDrawerClosing = false;
    this.showPreviewDrawer = true;
  }

  closePreviewDrawer(): void {
    clearTimeout(this.drawerCloseTimer);
    this.isDrawerClosing = true;
    this.drawerCloseTimer = setTimeout(() => {
      this.showPreviewDrawer = false;
      this.isDrawerClosing = false;
    }, 290);
  }

  onMemberCountChange(count: number): void {
    this.segmentMembersCount = count;
  }

  cancelar() {
    this.router.navigate(['/segmentos']);
  }

  onSegmentGone(): void {
    this.toast.info({
      title: 'Segmento no encontrado',
      message: 'El segmento ya no existe o fue eliminado por otro usuario.',
    });
    this.router.navigate(['/segmentos']);
  }

  get isSegmentEmpty(): boolean {
    return this.isEditing &&
      (this.staticPreview?.membersTotalElements === 0) &&
      !(this.staticPreview?.isLoadingMembers);
  }

  async eliminarSegmento(): Promise<void> {
    if (!this.segmentId) return;
    const confirmed = await this.confirmDialog.confirm({
      title: 'Eliminar segmento',
      message: 'Este segmento no tiene miembros activos. ¿Deseas eliminarlo permanentemente?',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;

    this.isDeletingSegment = true;
    this.segmentService.deleteSegment(this.segmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.success({ title: 'Segmento eliminado', message: 'El segmento fue eliminado correctamente.' });
          this.router.navigate(['/segmentos']);
        },
        error: () => {
          this.isDeletingSegment = false;
          this.toast.error({ title: 'Error', message: 'No se pudo eliminar el segmento.' });
        },
      });
  }
}
